import './util/handleError';
import './util/setupServiceWorker';
import './global/init';

import { maybeInjectCrmSession, postReadyToCrm } from './util/crmBridge';
import { listenForCrmChatOpen } from './util/crmOpenChat';
import { listenForCrmHarvest } from './util/crmEnrichHarvester';
import { registerCrmInboundReporter } from './util/crmInboundReporter';
import { registerCrmChatSelectReporter } from './util/crmChatSelectReporter';
import { registerCrmReadReporter } from './util/crmReadReporter';
import { registerCrmOutboxReporter } from './util/crmOutboxReporter';

// The WSS-rewrite patch lives in apps/telegram-tt/src/util/crmBridge.ts's
// applyProxyPatch and runs inside the gramjs worker (where the WebSocket
// is actually instantiated). The earlier crmProxyPatch.ts main-thread
// shim never had any effect since gramjs lives in the worker realm.

// WebRTC kill-switch is now implemented at the browser permission layer
// via Permissions-Policy headers (nginx) + iframe allow="" (parent SPA).
// The previous JS-level override (window.RTCPeerConnection = stub) is
// gone because Chrome refuses to redefine the property (configurable:
// false), the override would fail with `TypeError: Cannot redefine
// property` and pollute the console without actually blocking anything.

import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal,
} from './global';

import {
  DEBUG, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { selectChat, selectCurrentMessageList, selectPeerFullInfo, selectTabState } from './global/selectors';
import { selectSharedSettings } from './global/selectors/sharedState';
import { betterView } from './util/betterView';
import { IS_TAURI } from './util/browser/globalEnvironment';
import listenOtherClients from './util/browser/listenOtherClients';
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/browser/multitab';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { MULTITAB_STORAGE_KEY } from './util/multiaccount';
import { checkAndAssignPermanentWebVersion } from './util/permanentWebVersion';
import { onBeforeUnload } from './util/schedulers';
import initTauriApi from './util/tauri/initTauriApi';
import setupTauriListeners from './util/tauri/setupTauriListeners';
import updateWebmanifest from './util/updateWebmanifest';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

if (IS_TAURI) {
  initTauriApi();
  setupTauriListeners();
}

init();

async function init() {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  // CRM-iframe mode: pull the auth keys from the parent SPA via postMessage
  // and write them to localStorage in telegram-tt's "legacy single-account"
  // format BEFORE the rest of init() reads the session. No-op when running
  // standalone (no `accountId` query param).
  await maybeInjectCrmSession();

  checkAndAssignPermanentWebVersion();
  listenOtherClients();

  subscribeToMultitabBroadcastChannel();
  await requestGlobal(APP_VERSION);
  localStorage.setItem(MULTITAB_STORAGE_KEY, '1');
  onBeforeUnload(() => {
    const global = getGlobal();
    if (Object.keys(global.byTabId).length === 1) {
      localStorage.removeItem(MULTITAB_STORAGE_KEY);
    }
  });

  await initGlobal();
  getActions().init();

  getActions().updateShouldEnableDebugLog();
  getActions().updateShouldDebugExportedSenders();

  const global = getGlobal();

  initLocalization(selectSharedSettings(global).language, true);

  subscribeToMasterChange((isMasterTab) => {
    getActions()
      .switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true });
  });
  const shouldReestablishMasterToSelf = getGlobal().auth.state !== 'authorizationStateReady';
  establishMultitabRole(shouldReestablishMasterToSelf);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    updateWebmanifest();

    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );

    betterView();
  });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  // Notify the parent SPA that the iframe is alive — lets the CRM hide its
  // "Подключение к Telegram..." overlay. No-op outside iframe mode.
  postReadyToCrm();
  // Start handling lead-drawer deep-links (tg:openChat). No-op outside
  // iframe mode (no crmOrigin / no parent window).
  listenForCrmChatOpen();
  // Start handling profile-harvest requests (tg:harvestUser) — read the peer's
  // fullInfo from this session's state and post it back (no 2nd session).
  listenForCrmHarvest();
  // Start forwarding live inbound/outbound DMs + the account's own profile to
  // the CRM (realtime lead create + fill-once primary). No-op outside iframe.
  registerCrmInboundReporter();
  // Start forwarding the operator's current chat selection so the CRM inbox
  // sidebar can show the matching lead. No-op outside iframe mode.
  registerCrmChatSelectReporter();
  // Start forwarding the inbox read marker (tg:chatRead) so the CRM clears a
  // lead's unread badge the instant the operator reads the chat. No-op outside
  // iframe mode.
  registerCrmReadReporter();
  // Start forwarding our outgoing send + the peer's outbox-read marker
  // (tg:messageSent / tg:outboxRead) so the CRM chat button shows ✓/✓✓ the
  // instant we send / the lead reads. No-op outside iframe mode.
  registerCrmOutboxReporter();

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      const currentGlobal = getGlobal();
      const currentMessageList = selectCurrentMessageList(currentGlobal);
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(currentGlobal));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', currentGlobal);
      if (currentMessageList) {
        // eslint-disable-next-line no-console
        console.warn(
          'CURRENT MESSAGE LIST',
          selectChat(currentGlobal, currentMessageList.chatId),
          selectPeerFullInfo(currentGlobal, currentMessageList.chatId),
          currentGlobal.messages.byChatId[currentMessageList.chatId],
        );
      }
    });
  }
}

onBeforeUnload(() => {
  const actions = getActions();
  actions.leaveGroupCall?.({ isPageUnload: true });
  actions.hangUp?.({ isPageUnload: true });
});
